import { Grid } from "@material-ui/core";
import { useRouter } from "@uirouter/react";
import { useMemo } from "react";
import { useForm } from "react-hook-form";
import { useSelector } from "react-redux";
import styled from "styled-components";
import { v4 } from "uuid";
import { ErrorText, useHasCredit } from "../../../components/billing";
import { Form } from "../../../components/forms";
import { Checkbox } from "../../../components/forms/checkbox";
import { EditableField } from "../../../components/forms/field";
import { useAppLoader } from "../../../components/loader/app-loader-hook";
import { Widget } from "../../../components/widget";
import { AppStore } from "../../../store";
import { AuthDTO } from "../../../store/auth/models/dto";
import { useFetchable } from "../../../store/common/fetchable";
import {
  Fetchable,
  isFetchableSuccess,
} from "../../../store/common/fetchable/helpers";
import { Users } from "../../../store/users/reducer";
import { VerificationDTO } from "../../../store/verification/models/dto/aml";
import { VerificationService } from "../../../store/verification/service";
import { getOption, useYupValidationResolver } from "../../../utils/utils";
import {
  CTA,
  Section,
  StyledDateField,
  StyledTextField,
} from "../../profile/base";
import { FraudRoutes } from "../routes";

type RequestDTO = VerificationDTO.Requests.UploadIndividualAml.T;
const { useFields, defaults } = VerificationDTO.Requests.UploadIndividualAml;

const StyledWidget = styled(Widget)`
  height: auto;
  display: flex;
  flex-direction: column;
`;

export const NewIndividualAml = () => {
  const { labels, validation } = useFields();
  const resolver = useYupValidationResolver(validation);
  const currentUser = useFetchable(Users.current);
  const currentAuth = useSelector<
    AppStore,
    Fetchable<AuthDTO.Responses.Login.T>
  >((store) => store.auth.login);

  const {
    handleSubmit,
    control,
    formState: { errors },
    setValue,
  } = useForm<RequestDTO>({
    defaultValues: { ...defaults, fraudCheckId: v4() },
    resolver,
  });
  const router = useRouter();

  const loadable = useAppLoader();

  const onSubmit = (data: RequestDTO) =>
    loadable(
      VerificationService.postIndividualAml(data)
        .then(() => router.stateService.go(FraudRoutes.names.allIndividualAml))
        .catch(() => {
          setValue("fraudCheckId", v4());
        })
    );

  const hasCredit = useHasCredit("INDIVIDUAL_AML");

  const isAdmin = useMemo(
    () =>
      isFetchableSuccess(currentAuth) &&
      currentAuth.data.accessToken.roles.includes("ROLE_ADMIN"),
    [currentAuth]
  );

  const isMerchant = useMemo(
    () =>
      isFetchableSuccess(currentAuth) &&
      currentAuth.data.accessToken.roles.includes("ROLE_MERCHANT"),
    [currentAuth]
  );

  const cta = useMemo(
    () =>
      getOption(currentUser).map((curr) => {
        if (!hasCredit && !isAdmin) {
          return (
            <>
              <CTA disabled>Verify this User</CTA>
              <ErrorText>Please Top Up your Account</ErrorText>
            </>
          );
        }
        if (curr.kycStatus === "VERIFIED" || isAdmin || isMerchant) {
          return <CTA>Verify this User</CTA>;
        }
        return (
          <>
            <CTA disabled>Verify this User</CTA>
            <ErrorText>
              You cannot perform this action because your KYC status is not yet
              verified.
            </ErrorText>
          </>
        );
      }),
    [hasCredit, currentUser, isAdmin]
  );

  return cta.fold(
    () => null,
    (x) => (
      <StyledWidget title="Individual AML">
        <Form onSubmit={handleSubmit(onSubmit)}>
          <Section container spacing={3}>
            <Grid item xs={12} md={6}>
              <EditableField editing control={control} name="firstName">
                <StyledTextField
                  fullWidth
                  type="text"
                  label={labels.firstName}
                  error={!!errors.firstName}
                  helperText={errors.firstName?.message}
                />
              </EditableField>
            </Grid>
            <Grid item xs={12} md={6}>
              <EditableField editing control={control} name="middleName">
                <StyledTextField
                  fullWidth
                  type="text"
                  label={labels.middleName}
                  error={!!errors.middleName}
                  helperText={errors.middleName?.message}
                />
              </EditableField>
            </Grid>
            <Grid item xs={12} md={6}>
              <EditableField editing control={control} name="lastName">
                <StyledTextField
                  fullWidth
                  type="text"
                  label={labels.lastName}
                  error={!!errors.lastName}
                  helperText={errors.lastName?.message}
                />
              </EditableField>
            </Grid>
            <Grid item xs={12} md={6}>
              <EditableField editing control={control} name="dateOfBirth">
                <StyledDateField
                  fullWidth
                  label={labels.dateOfBirth}
                  error={!!errors.dateOfBirth}
                  helperText={errors.dateOfBirth?.message}
                />
              </EditableField>
            </Grid>
            <Grid item xs={12}>
              <EditableField editing control={control} name="ongoingChecks">
                <Checkbox
                  label="Keep doing checks Regularly"
                  error={!!errors.ongoingChecks}
                  helperText={errors.ongoingChecks?.message}
                />
              </EditableField>
            </Grid>
          </Section>
          {x}
        </Form>
      </StyledWidget>
    )
  );
};
